import moment from 'moment';

const UNAVAILABLE_COLOR = '#D0D0D0';

const normalizeDate = date => moment(date).format('YYYY-MM-DD');
const normalizeMonthDay = date => moment(date).format('MM-DD');

const sortAvailability = availability =>
  [...(availability || [])].sort((a, b) =>
    moment(a.startTime, 'HH:mm').diff(moment(b.startTime, 'HH:mm'))
  );

export const getEffectiveAvailability = (date, weeklyConfig = [], yearlyConfig = []) => {
  const monthDayKey = normalizeMonthDay(date);
  const yearly = yearlyConfig?.find(item => normalizeMonthDay(item?.day) === monthDayKey);

  if(yearly) {
    return sortAvailability(yearly.availability);
  }

  const dayOfWeek = moment(date).day();
  const weekly = weeklyConfig?.find(item => +item?.dayId === dayOfWeek);

  return sortAvailability(weekly?.availability);
};

export const isDayFullyUnavailable = (date, weeklyConfig, yearlyConfig) => {
  const availability = getEffectiveAvailability(date, weeklyConfig, yearlyConfig);
  return !availability?.length;
};

export const isDateTimeUnavailable = (date, weeklyConfig, yearlyConfig) => {
  const availability = getEffectiveAvailability(date, weeklyConfig, yearlyConfig);

  if(!availability?.length) {
    return true;
  }

  const clicked = moment(date);
  const dateKey = normalizeDate(date);

  const isInsideAvailableWindow = availability.some(window => {
    const windowStart = moment(`${dateKey} ${window.startTime}:00`, 'YYYY-MM-DD HH:mm:ss');
    const windowEnd = moment(`${dateKey} ${window.endTime}:00`, 'YYYY-MM-DD HH:mm:ss');

    if(window.endTime === '23:59') {
      windowEnd.add(1, 'minute');
    }

    return clicked.isSameOrAfter(windowStart) && clicked.isBefore(windowEnd);
  });

  return !isInsideAvailableWindow;
};

export const isClickUnavailable = (date, allDay, weeklyConfig, yearlyConfig) => {
  if(allDay) {
    return isDayFullyUnavailable(date, weeklyConfig, yearlyConfig);
  }

  return isDateTimeUnavailable(date, weeklyConfig, yearlyConfig);
};

export const invertAvailabilityToUnavailableSlots = (date, availability = []) => {
  const dateKey = normalizeDate(date);
  const dayStart = moment(`${dateKey} 00:00:00`, 'YYYY-MM-DD HH:mm:ss');
  const dayEnd = moment(`${dateKey} 00:00:00`, 'YYYY-MM-DD HH:mm:ss').add(1, 'day');
  const sorted = sortAvailability(availability);

  if(!sorted.length) {
    return [{
      start: dayStart.format('YYYY-MM-DD HH:mm:ss'),
      end: dayEnd.format('YYYY-MM-DD HH:mm:ss')
    }];
  }

  const slots = [];
  let cursor = dayStart.clone();

  sorted.forEach(window => {
    const windowStart = moment(`${dateKey} ${window.startTime}:00`, 'YYYY-MM-DD HH:mm:ss');
    const windowEnd = moment(`${dateKey} ${window.endTime}:00`, 'YYYY-MM-DD HH:mm:ss');

    if(windowEnd.format('HH:mm') === '23:59') {
      windowEnd.add(1, 'minute');
    }

    if(windowStart.isAfter(cursor)) {
      slots.push({
        start: cursor.format('YYYY-MM-DD HH:mm:ss'),
        end: windowStart.format('YYYY-MM-DD HH:mm:ss')
      });
    }

    if(windowEnd.isAfter(cursor)) {
      cursor = windowEnd.clone();
    }
  });

  if(cursor.isBefore(dayEnd)) {
    slots.push({
      start: cursor.format('YYYY-MM-DD HH:mm:ss'),
      end: dayEnd.format('YYYY-MM-DD HH:mm:ss')
    });
  }

  return slots;
};

export const buildUnavailableBackgroundEvents = (weeklyConfig, yearlyConfig, rangeStart, rangeEnd) => {
  if(!rangeStart || !rangeEnd) {
    return [];
  }

  const events = [];
  const cursor = moment(rangeStart).startOf('day');
  const end = moment(rangeEnd).startOf('day');

  while(cursor.isBefore(end)) {
    const availability = getEffectiveAvailability(cursor, weeklyConfig, yearlyConfig);
    const unavailableSlots = invertAvailabilityToUnavailableSlots(cursor, availability);

    unavailableSlots.forEach((slot, index) => {
      events.push({
        id: `unavailable-${normalizeDate(cursor)}-${index}`,
        start: slot.start,
        end: slot.end,
        display: 'background',
        backgroundColor: UNAVAILABLE_COLOR,
        classNames: ['fc-unavailable-slot'],
        editable: false,
        overlap: true,
        extendedProps: {
          isUnavailableSlot: true
        }
      });
    });

    cursor.add(1, 'day');
  }

  return events;
};

export const getUnavailableDayCellClassNames = (date, weeklyConfig, yearlyConfig) => {
  if(isDayFullyUnavailable(date, weeklyConfig, yearlyConfig)) {
    return ['fc-day-unavailable'];
  }

  return [];
};
